import React, { useState } from 'react';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import styled from 'styled-components';
import { PageCard, PageContainer, Poppins, Settings, Spacer, TableSkeleton } from 'src/common';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import Button from 'src/components/form/Button';
import {
  ConfirmDeleteModal,
  LinkScenariosToControlModal,
  PdfExportModalRM,
  RiskManagementModal,
  UpdateControlsModal,
} from 'src/components/modals';
import _ from 'lodash';
import { useColumns } from './useColumns';
import GenericTable, { genEmtyRowTextComponent, GTRowEmpty } from 'src/components/GenericTable';
import { ControlTableExpandContent } from './comps';
import { AddRmpModal } from 'src/components/modals/AddRmpModal';
import { Alert } from '@mui/material';
import { downloadCsv, pickFilter } from './util';
import CommonMenu from 'src/components/CommonMenu';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { WidgetRMP } from 'src/components/widgets';
import { auditEvent } from 'src/api/other';
import { AuditSection } from './sections';
import { ControlSelectionInput, useControlsQuery, useDeleteControlsMutation } from 'src/graphql/_generated';
import { isQueryLoading, useCache } from 'src/graphql';
import { checkIsModifierStrategy } from 'src/components/Modifier/util';
import Filters, { Indicators } from './filters';
import { FiltersProvider, useFiltersState } from './filters/state';
import { useAuth } from 'src/state/auth';

const Div = styled(PageContainer)`
  .panel {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    grid-gap: 10px;

    &__btns {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      grid-gap: 10px;
      flex-wrap: wrap;
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
  }
  .success {
    color: ${({ theme }) => theme.colors.success};
    font-size: 14px;
  }
`;

const RiskManagementPageContent = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isExportPdfModalOpen, setIsExportPdfModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<React.MouseEvent['currentTarget'] | null>(null);
  const { refetchQuery } = useCache();
  const { filters, isInit } = useFiltersState();
  const { features } = useAuth();
  useGaEventPageView('Cydea | Risk Management');

  const appliedFilters = isInit ? filters : undefined;

  const selection: ControlSelectionInput = {
    strategy: appliedFilters?.strategy.map(pickFilter),
    status: appliedFilters?.status.map(pickFilter),
    owners: appliedFilters?.owners.map(pickFilter),
    frameworks: appliedFilters?.frameworks.map(pickFilter),
    assessments: appliedFilters?.assessments.map(pickFilter),
    existingMaturity: appliedFilters?.existingMaturity.map(pickFilter),
    targetMaturity: appliedFilters?.targetMaturity.map(pickFilter),
  };

  const { data, loading, networkStatus, previousData } = useControlsQuery({
    variables: { selection },
    notifyOnNetworkStatusChange: true,
  });

  const controls = _.orderBy((data || previousData)?.controls, ['meta.createdAt'], ['desc']);

  const isInitialLoading = isQueryLoading({ loading, data, networkStatus }) && !previousData;
  const isLoading = isInitialLoading;

  const {
    columns,
    GTColumnSelectAnchorExported,
    setDeleteModal,
    deleteModal,
    linkScenarioModal,
    setLinkScenarioModal,
    controlEditId,
    setControlEditId,
    checked,
    resetChecked,
  } = useColumns({ data: controls });

  const selectedControlData = controls.filter((control) => checked.includes(control.id));

  const [deleteControls] = useDeleteControlsMutation({
    onCompleted: () => {
      queryCache.invalidateQueries([QKeys.Audit]);
      refetchQuery({ include: ['controls'] });
      setDeleteModal({ id: null, hasScenarios: 0 });
      setIsUpdateModalOpen(false);
      resetChecked();
    },
  });

  const onMenuClick = (e: React.MouseEvent<HTMLLIElement>, cb: () => void) => {
    e.stopPropagation();
    handleClose();
    cb();
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Meta title={`Cydea | Risk Management`} feedbackScript />
      {isAddModalOpen && <AddRmpModal onClose={() => setIsAddModalOpen(false)} />}
      {controlEditId && <RiskManagementModal controlId={controlEditId} onClose={() => setControlEditId(undefined)} invalidateQueries={['controls']} />}
      {deleteModal.id && (
        <ConfirmDeleteModal
          onClose={() => setDeleteModal({ id: null })}
          onConfirm={() => deleteControls({ variables: { ids: [deleteModal.id!] } })}
        >
          {!!deleteModal.hasScenarios && (
            <Alert severity="warning">
              {deleteModal.hasScenarios > 1
                ? 'This control is linked to scenarios.'
                : 'This control is linked to a scenario.'}
            </Alert>
          )}
          {deleteModal.isLive && <Alert severity="warning">This control is already live.</Alert>}
        </ConfirmDeleteModal>
      )}
      {isExportPdfModalOpen && <PdfExportModalRM onClose={() => setIsExportPdfModalOpen(false)} controls={controls} />}
      {isUpdateModalOpen && (
        <UpdateControlsModal
          initialValues={selectedControlData}
          onClose={() => setIsUpdateModalOpen(false)}
          onDelete={(ids) => deleteControls({ variables: { ids } })}
          isLoading={loading}
        />
      )}
      {linkScenarioModal.id && (
        <LinkScenariosToControlModal
          controlId={linkScenarioModal.id}
          isModifierStrategyType={checkIsModifierStrategy(linkScenarioModal.strategy)}
          onClose={() => setLinkScenarioModal({ id: null })}
          invalidateQueries={['controls']}
          subTitle={{
            title: 'CONTROL',
            value: linkScenarioModal?.name || '',
          }}
        />
      )}

      <Div>
        <div className="panel">
          <Poppins px={32} weight={600}>
            Risk Management
          </Poppins>
          <div className="panel__btns">
            {selectedControlData.length ? (
              <Button
                onClick={() => {
                  setIsUpdateModalOpen(true);
                  mpEvent(MPEvents.ButtonClick, {
                    button: 'Edit selected controls',
                    tags: ['RMP'],
                  });
                }}
                $constWidth={270}
                primary
                disabled={!selectedControlData.length}
                data-cy="edit-selected-controls-btn"
              >
                EDIT SELECTED CONTROLS
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => {
                    setIsAddModalOpen(true);
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Add control',
                      tags: ['RMP'],
                    });
                  }}
                  $constWidth={200}
                  primary
                  data-cy="add-control"
                >
                  + ADD CONTROL
                </Button>
                {features['app.filters.rmp'] && <Filters />}
                <Settings
                  active={!!anchorEl}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                  }}
                  data-cy="common-menu"
                  variant="button"
                />
                <CommonMenu
                  onCsvClick={(e) =>
                    onMenuClick(e, () => {
                      downloadCsv(controls);
                      auditEvent({
                        action: 'ExportCsv',
                        resource: 'control',
                        control_id: controls.map((el) => el.id),
                      });
                    })
                  }
                  onPdfClick={(e) => onMenuClick(e, () => setIsExportPdfModalOpen(true))}
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                />
              </>
            )}
          </div>
        </div>
        <Spacer $px={15} />
        {appliedFilters && <Indicators />}
        <Spacer $px={15} />
        {!isLoading ? (
          <>
            <WidgetRMP controls={controls} />
            <Spacer $px={18} />
            <GenericTable
              tableId={TableIds.rmp}
              data={controls}
              columns={columns}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
              expandContent={(control) => {
                if (!control.riskManagementPlansCount) return null;
                return <ControlTableExpandContent id={control.id} />;
              }}
              hideExpandContentWhenClosed
              searchable
              emptyRow={
                <GTRowEmpty
                  text={genEmtyRowTextComponent({
                    onClick: () => setIsAddModalOpen(true),
                    endText: 'to add controls.',
                  })}
                />
              }
            />

            <Spacer $px={30} />
            <PageCard
              style={{
                paddingTop: 0,
                paddingBottom: 0,
              }}
            >
              <AuditSection />
            </PageCard>
          </>
        ) : (
          <TableSkeleton />
        )}
      </Div>
    </>
  );
};

const RiskManagementPage = () => {
  return (
    <FiltersProvider>
      <RiskManagementPageContent />
    </FiltersProvider>
  );
};

export default RiskManagementPage;
